// search-toggle.ts（排他オープン + 親子チェック同期）
type ToggleTarget = HTMLElement & { dataset: DOMStringMap };

function findBoxForButton(btn: HTMLButtonElement): HTMLElement | null {
  const sel = (btn as ToggleTarget).dataset.target;
  if (sel) return document.querySelector<HTMLElement>(sel);

  const container =
    btn.closest<HTMLElement>('[data-search-container]') ??
    btn.parentElement ??
    document.body;
  return container.querySelector<HTMLElement>('.search-box');
}

function showBox(btn: HTMLButtonElement, box: HTMLElement) {
  box.classList.remove('hidden');
  box.setAttribute('aria-hidden', 'false');
  btn.setAttribute('aria-expanded', 'true');
  btn.classList.add('is-active');

  const close = box.querySelector<HTMLButtonElement>('.search-close');
  (close ?? box).focus();
}

function hideBox(btn: HTMLButtonElement, box: HTMLElement) {
  box.classList.add('hidden');
  box.setAttribute('aria-hidden', 'true');
  btn.setAttribute('aria-expanded', 'false');
  btn.classList.remove('is-active');
}

function applyA11y(btn: HTMLButtonElement, box: HTMLElement) {
  btn.setAttribute(
    'aria-expanded',
    box.classList.contains('hidden') ? 'false' : 'true'
  );
  if (!btn.hasAttribute('aria-controls')) {
    if (!box.id)
      box.id = `search-box-${Math.random().toString(36).slice(2, 8)}`;
    btn.setAttribute('aria-controls', box.id);
  }
  box.setAttribute(
    'aria-hidden',
    box.classList.contains('hidden') ? 'true' : 'false'
  );
  if (!box.getAttribute('role')) box.setAttribute('role', 'dialog');
}

/** 追加：親チェックと子チェックの同期ロジック */
function setupCategorySync(box: HTMLElement) {
  // 親（全選択）として扱うラベル内のチェックボックス
  const controllerSelector =
    'label.parent input[type="checkbox"], label.search-form__child-taxonomy-all-label input[type="checkbox"]';

  /** コントローラ（親）の属する最小グループ要素を推定（その中に子[data-id]がある） */
  function findGroupForController(ctrl: HTMLInputElement): HTMLElement {
    let el: HTMLElement | null =
      ctrl.closest<HTMLElement>('label') ??
      (ctrl.parentElement as HTMLElement | null);
    while (el && el !== box) {
      if (el.querySelector('input[type="checkbox"][data-id]')) return el;
      el = el.parentElement;
    }
    return box; // フォールバック：ボックス全体
  }

  /** 子チェックの属するグループ（＝一番近いコントローラを含む領域）を推定 */
  function findGroupForChild(child: HTMLInputElement): HTMLElement {
    let el: HTMLElement | null = child.parentElement as HTMLElement | null;
    while (el && el !== box) {
      if (el.querySelector(controllerSelector)) return el;
      el = el.parentElement;
    }
    // 近傍に親ラベルが見つからない場合は、子[data-id]を複数含む最小祖先を探す
    el = child.parentElement as HTMLElement | null;
    while (el && el !== box) {
      const kids = el.querySelectorAll<HTMLInputElement>(
        'input[type="checkbox"][data-id]'
      );
      if (kids.length > 0) return el;
      el = el.parentElement;
    }
    return box;
  }

  /** グループ内の子チェックボックスを返す */
  function getChildrenInGroup(group: HTMLElement): HTMLInputElement[] {
    return Array.from(
      group.querySelectorAll<HTMLInputElement>(
        'input[type="checkbox"][data-id]'
      )
    );
  }

  /** グループ内のコントローラ（親）を返す（複数ある場合は最初） */
  function getControllerInGroup(group: HTMLElement): HTMLInputElement | null {
    return group.querySelector<HTMLInputElement>(controllerSelector);
  }

  /** 子の状態から親の checked / indeterminate を更新 */
  function updateControllerFromChildren(group: HTMLElement) {
    const ctrl = getControllerInGroup(group);
    if (!ctrl) return;
    const children = getChildrenInGroup(group);
    if (children.length === 0) {
      ctrl.indeterminate = false;
      return;
    }
    const checkedCount = children.filter((c) => c.checked).length;
    ctrl.checked = checkedCount === children.length;
    ctrl.indeterminate = checkedCount > 0 && checkedCount < children.length;
  }

  // 親→子 反映
  box.addEventListener('change', (e) => {
    const target = e.target as HTMLElement | null;
    if (!target) return;

    const ctrl = target.closest<HTMLInputElement>(controllerSelector);
    if (ctrl) {
      const group = findGroupForController(ctrl);
      const children = getChildrenInGroup(group);
      children.forEach((c) => {
        c.checked = ctrl.checked;
        // changeイベントを発火させたい場合は下記を有効化
        // c.dispatchEvent(new Event('change', { bubbles: true }));
      });
      // 親の indeterminate を明示的にOFF（全選択 or 全解除直後）
      ctrl.indeterminate = false;
    }
  });

  // 子→親 反映
  box.addEventListener('change', (e) => {
    const target = e.target as HTMLInputElement | null;
    if (!target || !target.matches('input[type="checkbox"][data-id]')) return;
    const group = findGroupForChild(target);
    updateControllerFromChildren(group);
  });

  // 初期同期（読み込み時、既にURLクエリで子が選ばれている場合など）
  const groupsChecked = new Set<HTMLElement>();
  Array.from(
    box.querySelectorAll<HTMLInputElement>(controllerSelector)
  ).forEach((ctrl) => {
    const group = findGroupForController(ctrl);
    if (groupsChecked.has(group)) return;
    groupsChecked.add(group);
    updateControllerFromChildren(group);
  });
}

document.addEventListener('DOMContentLoaded', () => {
  const buttons = Array.from(
    document.querySelectorAll<HTMLButtonElement>('.search-btn')
  );

  const pairs = buttons
    .map((btn) => {
      const box = findBoxForButton(btn);
      if (!box) return null;
      applyA11y(btn, box);
      return { btn, box };
    })
    .filter(Boolean) as Array<{ btn: HTMLButtonElement; box: HTMLElement }>;

  const boxes = new Set(pairs.map((p) => p.box));

  function hideAllExcept(except?: HTMLElement) {
    pairs.forEach(({ btn, box }) => {
      if (box !== except && !box.classList.contains('hidden')) {
        hideBox(btn, box);
      }
    });
  }

  // 排他オープン
  pairs.forEach(({ btn, box }) => {
    // 親子チェック同期をこのボックスで有効化（★追加）
    setupCategorySync(box);

    btn.addEventListener('click', (e) => {
      e.preventDefault();
      const isHidden = box.classList.contains('hidden');
      if (isHidden) {
        hideAllExcept(box);
        showBox(btn, box);
      } else {
        hideBox(btn, box);
      }
    });

    const closeBtn = box.querySelector<HTMLButtonElement>('.search-close');
    closeBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      hideBox(btn, box);
      btn.focus();
    });

    box.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        hideBox(btn, box);
        btn.focus();
      }
    });
  });

  document.addEventListener('click', (e) => {
    const target = e.target as HTMLElement | null;
    if (!target) return;

    const clickedInsideBox = Array.from(boxes).some((box) =>
      box.contains(target)
    );
    if (clickedInsideBox) return;

    if (target.closest('.search-btn')) return;

    hideAllExcept();
  });

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    const opened = pairs.find(({ box }) => !box.classList.contains('hidden'));
    if (opened) {
      hideAllExcept();
      opened.btn.focus();
    }
  });
});
